// frontend/src/utils/notificationFx.js
//
// Cross-cutting "alert me!" effects: chime, document-title flicker, favicon
// swap. Designed to be safe to call from anywhere — silent failures, no
// throws, no globals leaked.
//
// Usage:
//   import { ding, startTabFlash, stopTabFlash, unlockAudio } from './notificationFx';
//   unlockAudio();              // wire to first user-interaction listener
//   ding();                     // fire on each new notification
//   startTabFlash(unreadCount); // when admin's tab is unfocused
//   stopTabFlash();             // when tab is focused or count hits 0

const DEFAULT_SOUND_URL = '/sounds/notification.mp3';

let audioEl = null;
let audioUnlocked = false;
let tabFlashHandle = null;
let originalTitle = null;
let originalFavicon = null;

// ---------- AUDIO ----------

function getAudio() {
  if (typeof window === 'undefined') return null;
  if (!audioEl) {
    try {
      audioEl = new Audio(DEFAULT_SOUND_URL);
      audioEl.preload = 'auto';
      audioEl.volume = 0.45; // subtle by default
    } catch {
      audioEl = null;
    }
  }
  return audioEl;
}

/**
 * Browsers block autoplay until the user has interacted with the page.
 * Call this once on the first user gesture (click, keydown, etc) so the
 * audio element is "primed" and later ding() calls succeed silently.
 */
export function unlockAudio() {
  if (audioUnlocked) return;
  const a = getAudio();
  if (!a) return;
  // Play muted, immediately pause — counts as "user-initiated" playback
  a.muted = true;
  const p = a.play();
  if (p && typeof p.then === 'function') {
    p.then(() => {
      a.pause();
      a.currentTime = 0;
      a.muted = false;
      audioUnlocked = true;
    }).catch(() => {
      // Will retry on next user gesture
    });
  }
}

/** Play the notification chime. No-op if blocked or unsupported. */
export function ding() {
  const a = getAudio();
  if (!a) return;
  try {
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay may still be blocked — fail silently.
      });
    }
  } catch {
    /* noop */
  }
}

/** Adjust chime volume (0..1). Persists for the session. */
export function setVolume(v) {
  const a = getAudio();
  if (!a) return;
  a.volume = Math.max(0, Math.min(1, Number(v) || 0));
}

// ---------- TAB FLASH ----------

function captureOriginals() {
  if (typeof document === 'undefined') return;
  if (originalTitle === null) originalTitle = document.title || '';
  if (originalFavicon === null) {
    const link = document.querySelector('link[rel*="icon"]');
    originalFavicon = link ? link.href : null;
  }
}

// Tiny inline red-dot favicon (16x16). Generated as data URL so we don't
// have to ship another asset. Used to overlay an alert state on the tab.
function buildAlertFavicon() {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 32;
  c.height = 32;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  // Background — same shape as a generic favicon
  ctx.fillStyle = '#1f2937'; // gray-800
  ctx.fillRect(0, 0, 32, 32);
  // Bell outline
  ctx.fillStyle = '#fbbf24'; // amber-400
  ctx.beginPath();
  ctx.arc(16, 14, 8, 0, Math.PI * 2);
  ctx.fill();
  // Red unread dot
  ctx.fillStyle = '#ef4444';
  ctx.beginPath();
  ctx.arc(24, 8, 6, 0, Math.PI * 2);
  ctx.fill();
  return c.toDataURL('image/png');
}

function setFavicon(href) {
  if (typeof document === 'undefined' || !href) return;
  let link = document.querySelector('link[rel*="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Start flashing the document title and favicon. Idempotent.
 * Only flashes while the tab is hidden — when the user comes back, focus
 * handler in the context will call stopTabFlash().
 *
 * @param {number} unreadCount - shown in the title prefix
 */
export function startTabFlash(unreadCount) {
  if (typeof document === 'undefined') return;
  captureOriginals();

  const alertIcon = buildAlertFavicon();
  let on = false;

  if (tabFlashHandle) clearInterval(tabFlashHandle);

  const tick = () => {
    on = !on;
    document.title = on
      ? `(${unreadCount}) New activity • ${originalTitle}`
      : `🔔 ${originalTitle}`;
    setFavicon(on ? alertIcon : (originalFavicon || alertIcon));
  };

  tick();
  tabFlashHandle = setInterval(tick, 1100);
}

/** Stop flashing and restore the original title + favicon. */
export function stopTabFlash() {
  if (tabFlashHandle) {
    clearInterval(tabFlashHandle);
    tabFlashHandle = null;
  }
  if (typeof document !== 'undefined') {
    if (originalTitle !== null) document.title = originalTitle;
    if (originalFavicon) setFavicon(originalFavicon);
  }
}
